import React, { useMemo, useState, useContext, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TableContainer,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────
// Stages
// ─────────────────────────────────────────────
const STAGE_OPTIONS = [
  "Hot Leads",
  "Warm Leads",
  "Cold Leads",
  "Contacted",
  "Demo Scheduled",
  "Demo Completed",
  "Proposal / Quotation Sent",
  "Negotiation",
  "Decision Pending",
  "Customer",
  "Lost",
  "On Hold",
  "Future",
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const isoToDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date)) return "";
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const hr = parseInt(hours);
  const ampm = hr >= 12 ? "PM" : "AM";
  const displayHr = hr % 12 || 12;
  return `${displayHr}:${minutes} ${ampm}`;
};

const formatDateTime = (date, time) => {
  if (!date) return "—";
  const d = isoToDDMMYYYY(date);
  const t = time ? formatTime(time) : "";
  return t ? `${d}  ${t}` : d;
};

// ─────────────────────────────────────────────
// Priority helpers
// ─────────────────────────────────────────────
const PRIORITY_CONFIG = {
  Hot: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    icon: <LocalFireDepartmentIcon sx={{ fontSize: 13 }} />,
  },
  Warm: {
    color: "#f97316",
    bg: "#fff7ed",
    border: "#fed7aa",
    icon: <TrendingUpIcon sx={{ fontSize: 13 }} />,
  },
  Cold: {
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: <HorizontalRuleIcon sx={{ fontSize: 13 }} />,
  },
};

const getPriorityFromStage = (stage) => {
  if (!stage) return null;
  const s = stage.toLowerCase();
  if (s.includes("hot") || s.includes("confirm") || s.includes("follow"))
    return "Hot";
  if (s.includes("warm") || s.includes("interest") || s.includes("prospect"))
    return "Warm";
  if (s.includes("cold") || s.includes("lead") || s.includes("new"))
    return "Cold";
  return null;
};

// ─────────────────────────────────────────────
// Trip mode colours
// ─────────────────────────────────────────────
const MODE_COLORS = {
  ROUND: { bg: "#ede9fe", color: "#7c3aed" },
  DROP: { bg: "#dcfce7", color: "#16a34a" },
  PICKUP: { bg: "#dbeafe", color: "#1d4ed8" },
};

// ─────────────────────────────────────────────
// FilterPill
// ─────────────────────────────────────────────
const FilterPill = ({ label, active, onClick, count }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.5,
      py: 0.5,
      borderRadius: "20px",
      border: active ? "1.5px solid #0891b2" : "1.5px solid #e0e0e0",
      bgcolor: active ? "#e0f7fa" : "#fafafa",
      color: active ? "#0891b2" : "#555",
      fontWeight: active ? 700 : 500,
      fontSize: "0.8rem",
      cursor: "pointer",
      transition: "all 0.15s ease",
      userSelect: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        borderColor: "#0891b2",
        bgcolor: "#e0f7fa",
        color: "#0891b2",
      },
    }}
  >
    {label}
    {count !== undefined && (
      <Box
        sx={{
          ml: 0.3,
          px: 0.7,
          py: 0.1,
          borderRadius: "10px",
          bgcolor: active ? "#0891b2" : "#e0e0e0",
          color: active ? "#fff" : "#666",
          fontSize: "0.7rem",
          fontWeight: 700,
          minWidth: 18,
          textAlign: "center",
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

// ─────────────────────────────────────────────
// DetailRow — used inside the right panel
// ─────────────────────────────────────────────
const DetailRow = ({ label, value, valueStyle = {} }) => (
  <Box sx={{ display: "flex", flexDirection: "column", mb: 1.2 }}>
    <Typography
      sx={{
        fontSize: "0.65rem",
        color: "#888",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        mb: 0.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.83rem",
        color: "#1a1a2e",
        fontWeight: 500,
        ...valueStyle,
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────
// EnquiryRow — single table row
// ─────────────────────────────────────────────
const EnquiryRow = ({ enquiry, isSelected, onClick, resolveUserName }) => {
  const priority = getPriorityFromStage(
    enquiry.customer_stage || enquiry.stage,
  );
  const priorityCfg = priority ? PRIORITY_CONFIG[priority] : null;
  const modeColor = MODE_COLORS[enquiry.trip_info?.mode] || {
    bg: "#f5f5f5",
    color: "#777",
  };

  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{
        cursor: "pointer",
        bgcolor: isSelected ? "#e0f7fa" : "inherit",
        borderLeft: isSelected ? "3px solid #0891b2" : "3px solid transparent",
        transition: "background 0.12s",
        "&:hover td": { bgcolor: isSelected ? "#e0f7fa" : "#f9fafb" },
      }}
    >
      {/* Enquiry ID */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Typography
          sx={{
            fontSize: "0.77rem",
            fontWeight: 700,
            color: "#0891b2",
            fontFamily: "monospace",
          }}
        >
          {enquiry.booking_id}
        </Typography>
        <Typography sx={{ fontSize: "0.67rem", color: "#aaa", mt: 0.2 }}>
          {enquiry.created_at ? isoToDDMMYYYY(enquiry.created_at) : ""}
        </Typography>
      </TableCell>

      {/* Customer */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Typography
          sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1a1a2e" }}
        >
          {enquiry.customer_name}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
          {enquiry.customer_mobile}
        </Typography>
      </TableCell>

      {/* Pickup */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Typography
          sx={{ fontSize: "0.78rem", fontWeight: 500, color: "#333" }}
        >
          {isoToDDMMYYYY(enquiry.trip_info?.pickup_date) || "—"}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#888" }}>
          {formatTime(enquiry.trip_info?.pickup_time)}
        </Typography>
      </TableCell>

      {/* Trip details */}
      <TableCell sx={{ py: 0.85, px: 1.2, maxWidth: 200 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "#333",
            wordBreak: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {enquiry.trip_details || "—"}
        </Typography>
      </TableCell>

      {/* Vehicle type */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>
          {enquiry.trip_info?.vehicle_type || "—"}
        </Typography>
      </TableCell>

      {/* Mode */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Box
          sx={{
            display: "inline-block",
            px: 1,
            py: 0.25,
            borderRadius: "8px",
            bgcolor: modeColor.bg,
            color: modeColor.color,
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: 0.3,
          }}
        >
          {enquiry.trip_info?.mode || "—"}
        </Box>
      </TableCell>

      {/* Quote */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Typography
          sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f766e" }}
        >
          {enquiry.amount
            ? `₹${parseFloat(enquiry.amount).toLocaleString("en-IN")}`
            : "—"}
        </Typography>
      </TableCell>

      {/* Priority */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        {priorityCfg ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              px: 1,
              py: 0.25,
              borderRadius: "8px",
              bgcolor: priorityCfg.bg,
              color: priorityCfg.color,
              border: `1px solid ${priorityCfg.border}`,
              fontSize: "0.68rem",
              fontWeight: 700,
            }}
          >
            {priorityCfg.icon}
            {priority}
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.72rem", color: "#ccc" }}>—</Typography>
        )}
      </TableCell>

      {/* Stage */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Chip
          label={enquiry.customer_stage || enquiry.stage || "—"}
          size="small"
          sx={{
            fontSize: "0.67rem",
            height: 20,
            bgcolor: "#f0f4ff",
            color: "#3730a3",
            fontWeight: 600,
            border: "1px solid #c7d2fe",
          }}
        />
      </TableCell>

      {/* ✅ Created On */}
      <TableCell sx={{ py: 0.85, px: 1.2, whiteSpace: "nowrap" }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#333" }}>
          {enquiry.booking_created_at
            ? new Date(enquiry.booking_created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#888" }}>
          {enquiry.booking_created_at
            ? new Date(enquiry.booking_created_at).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : ""}
        </Typography>
      </TableCell>

      {/* ✅ Created By */}
      <TableCell sx={{ py: 0.85, px: 1.2 }}>
        <Typography
          sx={{ fontSize: "0.78rem", color: "#333", fontWeight: 500 }}
        >
          {resolveUserName(enquiry.created_by)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

// ═════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════
export default function EnquiryDashboard({
  vehiclesData,
  selectedMonth,
  onRefresh,
  onNavigateToBooking,
}) {
  const { userInfo, allUsers } = useContext(AutoTaskAuthContext) || {};
  const resolveUserName = useCallback(
    (userId) => {
      if (!userId) return "—";
      const found = allUsers?.find((u) => u.id === userId);
      return found ? found.name : `User #${userId}`;
    },
    [allUsers],
  );

  const navigate = useNavigate();

  // ── Filter state ──────────────────────────
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [enqType, setEnqType] = useState("ALL"); // ALL | CONFIRMED | PENDING
  const [priority, setPriority] = useState("ALL"); // ALL | Hot | Warm | Cold
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [stageFilter, setStageFilter] = useState("ALL");

  // ── Detail / action state ─────────────────
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ─────────────────────────────────────────────────────
  // Flatten all enquiries from vehiclesData.
  // vehiclesData is already filtered to the selected month
  // by DashboardHandler. Enquiry bookings have type "E"
  // which the backend exposes as "Enquiry" / "Enquiry (P)"
  // in the `type` field of each booking object.
  // ─────────────────────────────────────────────────────
  const allEnquiries = useMemo(() => {
    const list = [];
    vehiclesData.forEach((vehicle) => {
      vehicle.bookings.forEach((booking) => {
        if (booking.type && booking.type.toLowerCase().includes("enquiry")) {
          list.push({
            ...booking,
            vehicle_id: vehicle.vehicle_id,
            vehicle_name: vehicle.vehicle_name,
            vehicle_number: vehicle.vehicle_number,
            vehicle_code: vehicle.vehicle_code,
          });
        }
      });
    });
    // Sort by pickup_date descending (most recent first)
    list.sort((a, b) => {
      const da = a.trip_info?.pickup_date || "";
      const db = b.trip_info?.pickup_date || "";
      return db.localeCompare(da);
    });
    return list;
  }, [vehiclesData]);

  // ── Quick-count badges for pills ──────────
  const counts = useMemo(
    () => ({
      total: allEnquiries.length,
      hot: allEnquiries.filter(
        (e) => getPriorityFromStage(e.customer_stage || e.stage) === "Hot",
      ).length,
      warm: allEnquiries.filter(
        (e) => getPriorityFromStage(e.customer_stage || e.stage) === "Warm",
      ).length,
      cold: allEnquiries.filter(
        (e) => getPriorityFromStage(e.customer_stage || e.stage) === "Cold",
      ).length,
      confirmed: allEnquiries.filter((e) => !e.is_pending).length,
      pending: allEnquiries.filter((e) => e.is_pending).length,
    }),
    [allEnquiries],
  );

  // ── Apply filters ─────────────────────────
  const filteredEnquiries = useMemo(() => {
    return allEnquiries.filter((enq) => {
      const pickupDate = enq.trip_info?.pickup_date || "";
      if (dateFrom && pickupDate && pickupDate < dateFrom) return false;
      if (dateTo && pickupDate && pickupDate > dateTo) return false;
      if (enqType === "CONFIRMED" && enq.is_pending) return false;
      if (enqType === "PENDING" && !enq.is_pending) return false;

      // Priority filter (still useful for hot/warm/cold grouping)
      if (priority !== "ALL") {
        if (getPriorityFromStage(enq.stage || enq.customer_stage) !== priority)
          return false;
      }

      // ✅ New: exact stage filter
      if (stageFilter !== "ALL") {
        const enqStage = enq.stage || enq.customer_stage || "";
        if (enqStage !== stageFilter) return false;
      }

      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const haystack = [
          enq.booking_id,
          enq.customer_name,
          enq.customer_mobile,
          enq.trip_details,
          enq.trip_info?.pickup_location,
          enq.trip_info?.drop_location,
          enq.vehicle_name,
          enq.stage,
          enq.customer_stage, // ✅ include stage in search too
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allEnquiries, dateFrom, dateTo, enqType, priority, stageFilter, search]);

  const hasActiveFilters =
    dateFrom ||
    dateTo ||
    enqType !== "ALL" ||
    priority !== "ALL" ||
    stageFilter !== "ALL" ||
    search;

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setEnqType("ALL");
    setPriority("ALL");
    setStageFilter("ALL");
    setSearch("");
  };

  // ── Delete ────────────────────────────────
  const handleDelete = async () => {
    if (!selectedEnquiry) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/travel/booking/${selectedEnquiry.booking_id}`,
      );
      if (res.data.success) {
        setSnackbar({
          open: true,
          message: "Enquiry deleted successfully",
          severity: "success",
        });
        setDeleteDialogOpen(false);
        setSelectedEnquiry(null);
        onRefresh();
      } else {
        setSnackbar({
          open: true,
          message: res.data.statusMessage || "Failed to delete",
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Network error. Could not delete.",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = () => {
    if (!selectedEnquiry) return;
    navigate("/travel/booking", {
      state: { booking_id: selectedEnquiry.booking_id },
    });
  };

  // ── Derived selected enquiry data ─────────
  const priorityOfSelected = selectedEnquiry
    ? getPriorityFromStage(
        selectedEnquiry.customer_stage || selectedEnquiry.stage,
      )
    : null;
  const priorityCfgSelected = priorityOfSelected
    ? PRIORITY_CONFIG[priorityOfSelected]
    : null;

  // ══════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════
  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* ══ LEFT — table panel ══ */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
          transition: "margin-right 0.3s ease-in-out",
          marginRight: selectedEnquiry ? { xs: 0, md: "380px" } : 0,
        }}
      >
        {/* ── Filter bar ── */}
        <Box
          sx={{
            px: { xs: 1, md: 2 },
            pt: 1.5,
            pb: 1,
            borderBottom: "1px solid #f0f0f0",
            bgcolor: "#fafbff",
          }}
        >
          {/* Quick-filter pills row */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 1,
            }}
          >
            <FilterPill
              label="Confirmed"
              active={enqType === "CONFIRMED"}
              onClick={() =>
                setEnqType((t) => (t === "CONFIRMED" ? "ALL" : "CONFIRMED"))
              }
              count={counts.confirmed}
            />
            <FilterPill
              label="Pending"
              active={enqType === "PENDING"}
              onClick={() =>
                setEnqType((t) => (t === "PENDING" ? "ALL" : "PENDING"))
              }
              count={counts.pending}
            />

            <Box sx={{ flex: 1 }} />

            {/* Search */}
            <TextField
              size="small"
              placeholder="Search enquiries…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                minWidth: 200,
                "& .MuiInputBase-root": {
                  fontSize: "0.8rem",
                  borderRadius: "20px",
                  bgcolor: "#fff",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "#aaa" }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Tooltip title={filtersOpen ? "Hide filters" : "More filters"}>
              <IconButton
                size="small"
                onClick={() => setFiltersOpen((o) => !o)}
                sx={{
                  border: `1.5px solid ${filtersOpen ? "#0891b2" : "#e0e0e0"}`,
                  bgcolor: filtersOpen ? "#e0f7fa" : "#fff",
                  color: filtersOpen ? "#0891b2" : "#555",
                  transition: "all 0.15s ease",
                }}
              >
                <FilterListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh data">
              <IconButton
                size="small"
                onClick={onRefresh}
                sx={{ border: "1.5px solid #e0e0e0", bgcolor: "#fff" }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Expanded filter row */}
          {filtersOpen && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
                pt: 0.5,
                animation: "fadeIn 0.18s ease",
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "translateY(-4px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <TextField
                size="small"
                label="Pickup from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 148,
                  "& .MuiInputBase-root": { fontSize: "0.8rem" },
                }}
              />
              <TextField
                size="small"
                label="Pickup to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dateFrom }}
                sx={{
                  minWidth: 148,
                  "& .MuiInputBase-root": { fontSize: "0.8rem" },
                }}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: "0.8rem" }}>Stage</InputLabel>
                <Select
                  value={stageFilter}
                  label="Stage"
                  onChange={(e) => setStageFilter(e.target.value)}
                  sx={{ fontSize: "0.8rem" }}
                >
                  <MenuItem value="ALL">All Stages</MenuItem>
                  {STAGE_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {hasActiveFilters && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClearFilters}
                  sx={{
                    fontSize: "0.75rem",
                    color: "#0891b2",
                    textTransform: "none",
                  }}
                >
                  Clear all
                </Button>
              )}

              <Box sx={{ ml: "auto" }}>
                <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>
                  Showing{" "}
                  <strong style={{ color: "#0891b2" }}>
                    {filteredEnquiries.length}
                  </strong>{" "}
                  of {counts.total} enquiries
                  {selectedMonth && (
                    <span style={{ color: "#aaa" }}> · {selectedMonth}</span>
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Table ── */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ flex: 1, overflow: "auto", borderRadius: 0 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "#f8fafc",
                    borderBottom: "2px solid #e2e8f0",
                  },
                }}
              >
                {[
                  ["Enquiry ID", "120px"],
                  ["Customer", "150px"],
                  ["Pickup Date", "110px"],
                  ["Trip Details", "200px"],
                  ["Vehicle Type", "110px"],
                  ["Mode", "80px"],
                  ["Quote", "90px"],
                  ["Priority", "90px"],
                  ["Stage", "120px"],
                  ["Created On", "130px"],
                  ["Created By", "120px"],
                ].map(([label, width]) => (
                  <TableCell
                    key={label}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      py: 0.9,
                      px: 1.2,
                      width,
                      color: "#475569",
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.map((enq) => (
                <EnquiryRow
                  key={enq.booking_id}
                  enquiry={enq}
                  isSelected={selectedEnquiry?.booking_id === enq.booking_id}
                  resolveUserName={resolveUserName}
                  onClick={() =>
                    setSelectedEnquiry((prev) =>
                      prev?.booking_id === enq.booking_id ? null : enq,
                    )
                  }
                />
              ))}

              {filteredEnquiries.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 6, border: 0 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "#ccc",
                      }}
                    >
                      <CalendarMonthIcon sx={{ fontSize: 44, opacity: 0.4 }} />
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#aaa",
                        }}
                      >
                        No enquiries found
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "#bbb" }}>
                        {hasActiveFilters
                          ? "Try adjusting your filters"
                          : `No enquiries recorded for ${selectedMonth}`}
                      </Typography>
                      {hasActiveFilters && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleClearFilters}
                          sx={{
                            mt: 0.5,
                            fontSize: "0.75rem",
                            textTransform: "none",
                            borderColor: "#0891b2",
                            color: "#0891b2",
                            borderRadius: "20px",
                          }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ══ RIGHT — detail panel ══ */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: { xs: "100%", md: "380px" },
          height: "100%",
          borderLeft: "1px solid #e2e8f0",
          p: { xs: 1.5, md: 2 },
          overflow: "auto",
          bgcolor: "background.paper",
          transform: selectedEnquiry ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease-in-out",
          boxShadow: selectedEnquiry ? { xs: 3, md: 2 } : 0,
          zIndex: 10,
        }}
      >
        {selectedEnquiry ? (
          <Box>
            {/* Panel header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1.5,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}
                >
                  Enquiry Details
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "#0891b2",
                    fontFamily: "monospace",
                    fontWeight: 600,
                    mt: 0.2,
                  }}
                >
                  {selectedEnquiry.booking_id}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setSelectedEnquiry(null)}
                sx={{ color: "#888" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Priority badge */}
            {priorityCfgSelected && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.2,
                  py: 0.4,
                  borderRadius: "20px",
                  bgcolor: priorityCfgSelected.bg,
                  color: priorityCfgSelected.color,
                  border: `1.5px solid ${priorityCfgSelected.border}`,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  mb: 1.5,
                }}
              >
                {priorityCfgSelected.icon}
                {priorityOfSelected} Priority
              </Box>
            )}

            {/* Action buttons */}
            <Grid container spacing={1} sx={{ mb: 1.5 }}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  size="small"
                  sx={{
                    fontSize: "0.78rem",
                    borderRadius: "8px",
                    textTransform: "none",
                    borderColor: "#0891b2",
                    color: "#0891b2",
                    "&:hover": { bgcolor: "#e0f7fa", borderColor: "#0891b2" },
                  }}
                >
                  Edit
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  size="small"
                  sx={{
                    fontSize: "0.78rem",
                    borderRadius: "8px",
                    textTransform: "none",
                  }}
                >
                  Delete
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 1.5 }} />

            {/* Customer card */}
            <Card
              variant="outlined"
              sx={{
                mb: 1.2,
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              <CardContent sx={{ p: 1.4, "&:last-child": { pb: 1.4 } }}>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.8,
                  }}
                >
                  Customer
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 0.3,
                  }}
                >
                  {selectedEnquiry.customer_name}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#555" }}>
                  📱 {selectedEnquiry.customer_mobile}
                </Typography>
                {(selectedEnquiry.customer_stage || selectedEnquiry.stage) && (
                  <Chip
                    label={
                      selectedEnquiry.customer_stage || selectedEnquiry.stage
                    }
                    size="small"
                    sx={{
                      mt: 0.8,
                      fontSize: "0.67rem",
                      height: 20,
                      bgcolor: "#f0f4ff",
                      color: "#3730a3",
                      border: "1px solid #c7d2fe",
                      fontWeight: 600,
                    }}
                  />
                )}
                {selectedEnquiry.customer_type && (
                  <Typography
                    sx={{ fontSize: "0.7rem", color: "#777", mt: 0.5 }}
                  >
                    Type: {selectedEnquiry.customer_type}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Trip info card */}
            <Card
              variant="outlined"
              sx={{
                mb: 1.2,
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              <CardContent sx={{ p: 1.4, "&:last-child": { pb: 1.4 } }}>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  Trip Info
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Pickup Date"
                      value={formatDateTime(
                        selectedEnquiry.trip_info?.pickup_date,
                        selectedEnquiry.trip_info?.pickup_time,
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Drop Date"
                      value={formatDateTime(
                        selectedEnquiry.trip_info?.drop_date,
                        selectedEnquiry.trip_info?.drop_time,
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Mode"
                      value={selectedEnquiry.trip_info?.mode}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="PAX"
                      value={selectedEnquiry.trip_info?.pax}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DetailRow
                      label="Pickup Location"
                      value={selectedEnquiry.trip_info?.pickup_location}
                    />
                  </Grid>
                  {selectedEnquiry.trip_info?.pickup_address &&
                    selectedEnquiry.trip_info?.pickup_address !==
                      selectedEnquiry.trip_info?.pickup_location && (
                      <Grid item xs={12}>
                        <DetailRow
                          label="Pickup Address"
                          value={selectedEnquiry.trip_info?.pickup_address}
                        />
                      </Grid>
                    )}
                  <Grid item xs={12}>
                    <DetailRow
                      label="Drop Location"
                      value={selectedEnquiry.trip_info?.drop_location}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Vehicle Type"
                      value={selectedEnquiry.trip_info?.vehicle_type}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Received Via"
                      value={selectedEnquiry.trip_info?.received_type}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Financials card */}
            <Card
              variant="outlined"
              sx={{
                mb: 1.2,
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              <CardContent sx={{ p: 1.4, "&:last-child": { pb: 1.4 } }}>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  Financials
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Quote"
                      value={
                        selectedEnquiry.amount
                          ? `₹${parseFloat(
                              selectedEnquiry.amount,
                            ).toLocaleString("en-IN")}`
                          : "—"
                      }
                      valueStyle={{
                        color: "#0f766e",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Advance"
                      value={
                        selectedEnquiry.trip_info?.advance
                          ? `₹${parseFloat(
                              selectedEnquiry.trip_info.advance,
                            ).toLocaleString("en-IN")}`
                          : "—"
                      }
                    />
                  </Grid>
                  {selectedEnquiry.trip_info?.payment_details && (
                    <Grid item xs={12}>
                      <DetailRow
                        label="Payment Details"
                        value={selectedEnquiry.trip_info?.payment_details}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Notes card */}
            {(selectedEnquiry.trip_details || selectedEnquiry.instructions) && (
              <Card
                variant="outlined"
                sx={{
                  mb: 1.2,
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <CardContent sx={{ p: 1.4, "&:last-child": { pb: 1.4 } }}>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 1,
                    }}
                  >
                    Notes
                  </Typography>
                  {selectedEnquiry.trip_details && (
                    <DetailRow
                      label="Trip Details"
                      value={selectedEnquiry.trip_details}
                    />
                  )}
                  {selectedEnquiry.instructions && (
                    <DetailRow
                      label="Instructions"
                      value={selectedEnquiry.instructions}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Status */}
            <Card
              variant="outlined"
              sx={{ borderRadius: "10px", border: "1px solid #e2e8f0" }}
            >
              <CardContent sx={{ p: 1.4, "&:last-child": { pb: 1.4 } }}>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.8,
                  }}
                >
                  Assignment Status
                </Typography>
                <Chip
                  label={
                    selectedEnquiry.is_pending
                      ? "Pending Assignment"
                      : "Confirmed"
                  }
                  size="small"
                  color={selectedEnquiry.is_pending ? "warning" : "success"}
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                />
                {selectedEnquiry.driver_name &&
                  selectedEnquiry.driver_name !== "Not Assigned" && (
                    <Box sx={{ mt: 1 }}>
                      <DetailRow
                        label="Driver"
                        value={`${selectedEnquiry.driver_name}${
                          selectedEnquiry.driver_phone
                            ? ` · ${selectedEnquiry.driver_phone}`
                            : ""
                        }`}
                      />
                    </Box>
                  )}
                {selectedEnquiry.vehicle_name &&
                  selectedEnquiry.vehicle_name !== "Not Assigned" && (
                    <DetailRow
                      label="Vehicle"
                      value={`${selectedEnquiry.vehicle_code} · ${selectedEnquiry.vehicle_name}`}
                    />
                  )}

                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Created On"
                      value={
                        selectedEnquiry.booking_created_at
                          ? new Date(
                              selectedEnquiry.booking_created_at,
                            ).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DetailRow
                      label="Created By"
                      value={resolveUserName(selectedEnquiry.created_by)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem" }}>
              Select an enquiry to view details
            </Typography>
          </Box>
        )}
      </Box>

      {/* ══ DELETE DIALOG ══ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Enquiry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this enquiry? This action cannot be
            undone.
            {selectedEnquiry && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  bgcolor: "#fef2f2",
                  borderRadius: 1,
                  border: "1px solid #fecaca",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {selectedEnquiry.booking_id}
                </Typography>
                <Typography variant="body2">
                  {selectedEnquiry.customer_name} ·{" "}
                  {selectedEnquiry.customer_mobile}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ SNACKBAR ══ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
